import { apiFetch, publicApiFetch } from '../httpClient';

const CREATE_TEAM_ENDPOINT =
  process.env.REACT_APP_TEAM_CREATE_ENDPOINT || '/api/teams';
const TEAM_LIST_ENDPOINT =
  process.env.REACT_APP_TEAM_LIST_ENDPOINT || '/api/teams';
const MY_TEAMS_ENDPOINT =
  process.env.REACT_APP_MY_TEAMS_ENDPOINT || '/api/teams/me';
const TEAM_UPDATE_ENDPOINT_TEMPLATE =
  process.env.REACT_APP_TEAM_UPDATE_ENDPOINT_TEMPLATE || '/api/teams/:teamId';
const TEAM_UPDATE_METHOD =
  process.env.REACT_APP_TEAM_UPDATE_METHOD || 'PATCH';
const TEAM_CLOSE_ENDPOINT_TEMPLATE =
  process.env.REACT_APP_TEAM_CLOSE_ENDPOINT_TEMPLATE || '/api/teams/:teamId';
const TEAM_LOGO_PRESIGNED_URL_ENDPOINT =
  process.env.REACT_APP_TEAM_LOGO_PRESIGNED_URL_ENDPOINT || '/api/uploads/presigned-url';

export async function createTeam(teamPayload) {
  return apiFetch(CREATE_TEAM_ENDPOINT, {
    method: 'POST',
    body: teamPayload,
  });
}

function buildPathFromTemplate(template, pathParams = {}) {
  return Object.entries(pathParams).reduce((path, [key, value]) => {
    return path
      .replace(`:${key}`, encodeURIComponent(String(value)))
      .replace(`{${key}}`, encodeURIComponent(String(value)));
  }, template);
}

// 팀 기본 정보(이름/요일/시간/연령/성별/소개/로고 URL)를 수정한다.
export async function updateTeamInfo({ teamId, payload }) {
  return apiFetch(
    buildPathFromTemplate(TEAM_UPDATE_ENDPOINT_TEMPLATE, {
      teamId,
    }),
    {
      method: TEAM_UPDATE_METHOD,
      body: payload,
    },
  );
}

export async function closeTeam({ teamId }) {
  return apiFetch(
    buildPathFromTemplate(TEAM_CLOSE_ENDPOINT_TEMPLATE, {
      teamId,
    }),
    {
      method: 'DELETE',
    },
  );
}

// 백엔드 enum 값을 화면 표기용 한글 라벨로 정규화한다.
function normalizeGenderTypeLabel(genderType) {
  if (genderType === 'MALE') {
    return '남성';
  }
  if (genderType === 'FEMALE') {
    return '여성';
  }
  if (genderType === 'MIXED') {
    return '혼성';
  }
  return genderType || '혼성';
}

function normalizeWeekdayLabel(weekday) {
  const normalized = String(weekday || '').trim().toUpperCase();
  if (normalized === 'SUNDAY' || weekday === '일') {
    return '일';
  }
  if (normalized === 'MONDAY' || weekday === '월') {
    return '월';
  }
  if (normalized === 'TUESDAY' || weekday === '화') {
    return '화';
  }
  if (normalized === 'WEDNESDAY' || weekday === '수') {
    return '수';
  }
  if (normalized === 'THURSDAY' || weekday === '목') {
    return '목';
  }
  if (normalized === 'FRIDAY' || weekday === '금') {
    return '금';
  }
  if (normalized === 'SATURDAY' || weekday === '토') {
    return '토';
  }
  return String(weekday || '').trim();
}

function normalizeTeamWeekdays(team) {
  const rawWeekdays = Array.isArray(team?.activityWeekdays)
    ? team.activityWeekdays
    : Array.isArray(team?.weekdays)
      ? team.weekdays
      : Array.isArray(team?.activityDays)
        ? team.activityDays
        : [];

  const normalizedWeekdays = rawWeekdays
    .map(normalizeWeekdayLabel)
    .filter(Boolean);

  return normalizedWeekdays.length > 0 ? normalizedWeekdays : ['수', '토'];
}

// 팀 조회 응답의 시간대 필드 케이스를 단일 문자열 형식으로 맞춘다.
function resolveTimeRange(team) {
  if (team?.timeRange) {
    return team.timeRange;
  }
  if (team?.preferredTimeSlot) {
    return team.preferredTimeSlot;
  }
  if (team?.startTime && team?.endTime) {
    return `${team.startTime} ~ ${team.endTime}`;
  }
  return '19:30 ~ 22:00';
}

// 서로 다른 응답 스펙(teamId/name/role 필드명 차이)을 단일 모델로 통일한다.
export function normalizeTeamItem(team) {
  const teamId = team?.id ?? team?.teamId ?? team?.workspaceId;
  return {
    id: teamId == null ? null : String(teamId),
    name: team?.name || team?.teamName || '이름 없는 팀',
    chair:
      team?.chairNickname ||
      team?.chairName ||
      team?.chair ||
      team?.ownerName ||
      '-',
    logoUrl: team?.logoUrl || team?.teamLogoUrl || team?.profileImageUrl || '',
    activityWeekdays: normalizeTeamWeekdays(team),
    timeRange: resolveTimeRange(team),
    ageBand: team?.ageBand || team?.averageAgeBand || '-',
    genderType: normalizeGenderTypeLabel(team?.genderType),
    description: team?.description || '',
    role: team?.role || team?.memberRole || team?.myRole || 'MEMBER',
    updatedAt: team?.updatedAt || team?.modifiedAt || null,
  };
}

// 페이지네이션/래핑 형태가 다른 목록 응답에서 실제 배열만 추출한다.
function extractTeamItems(responseData) {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.content)) {
    return responseData.content;
  }
  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }
  if (Array.isArray(responseData?.teams)) {
    return responseData.teams;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  return [];
}

// 내 팀 목록을 조회하고 화면에서 바로 쓸 수 있는 구조로 반환한다.
export async function getMyTeams() {
  const responseData = await apiFetch(MY_TEAMS_ENDPOINT, {
    method: 'GET',
  });

  return extractTeamItems(responseData)
    .map(normalizeTeamItem)
    .filter((team) => Boolean(team.id));
}

// 공개 팀 목록을 조회해 팀 찾기 화면에서 사용한다.
export async function getPublicTeams() {
  const responseData = await publicApiFetch(TEAM_LIST_ENDPOINT, {
    method: 'GET',
  });

  return extractTeamItems(responseData)
    .map(normalizeTeamItem)
    .filter((team) => Boolean(team.id));
}

function resolveTeamLogoUploadDescriptor(responseData) {
  const uploadUrl =
    responseData?.presignedUrl ||
    responseData?.uploadUrl ||
    responseData?.signedUrl ||
    responseData?.url;
  if (!uploadUrl) {
    throw new Error('로고 업로드 URL을 받지 못했습니다.');
  }

  const fileUrl =
    responseData?.fileUrl ||
    responseData?.publicUrl ||
    responseData?.resourceUrl ||
    uploadUrl.split('?')[0];

  return {
    method: responseData?.method || 'PUT',
    uploadUrl,
    fileUrl,
    contentType: responseData?.contentType || '',
    key: responseData?.key || '',
  };
}

async function requestTeamLogoPresignedUrl({ teamId, file }) {
  const responseData = await apiFetch(TEAM_LOGO_PRESIGNED_URL_ENDPOINT, {
    method: 'POST',
    body: {
      targetType: 'TEAM_LOGO',
      usage: 'TEAM_LOGO',
      teamId: Number(teamId),
      fileName: file.name,
      contentType: file.type || 'image/png',
    },
  });

  return resolveTeamLogoUploadDescriptor(responseData);
}

async function uploadFileByPresignedUrl(uploadDescriptor, file) {
  const headers = new Headers();
  if (uploadDescriptor.contentType) {
    headers.set('Content-Type', uploadDescriptor.contentType);
  } else if (file.type) {
    headers.set('Content-Type', file.type);
  }

  const response = await fetch(uploadDescriptor.uploadUrl, {
    method: uploadDescriptor.method || 'PUT',
    headers,
    body: file,
  });

  if (!response.ok) {
    throw new Error(`로고 업로드에 실패했습니다. (${response.status})`);
  }
}

// 팀 로고를 presigned URL 방식으로 업로드하고 저장용 URL을 반환한다.
export async function uploadTeamLogoForTeam({ teamId, file }) {
  if (!teamId) {
    throw new Error('팀 ID가 필요합니다.');
  }
  if (!file) {
    throw new Error('업로드할 로고 파일이 필요합니다.');
  }

  const uploadDescriptor = await requestTeamLogoPresignedUrl({ teamId, file });
  await uploadFileByPresignedUrl(uploadDescriptor, file);
  return {
    key: uploadDescriptor.key,
    fileUrl: uploadDescriptor.fileUrl,
    url: uploadDescriptor.fileUrl,
  };
}
